import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, RowInfo } from 'react-table';
import { Button, Table } from '@components/Common';
import { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { HeadingRow, Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import AdminNavigation from '../AdminNavigation';
import DeliveryDestinationEditor from './DeliveryDestinationEditor';

// TanStack Query hooks
import {
  useDeliveryDestinationsQuery,
  useCreateDeliveryDestinationMutation,
  useUpdateDeliveryDestinationMutation,
  DeliveryDestination
} from '@api/queries/deliveryDestinations';

const DeliveryDestinations: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DeliveryDestination | null>(null);

  // TanStack Query
  const { data: items = [], isLoading, refetch } = useDeliveryDestinationsQuery();
  const createMutation = useCreateDeliveryDestinationMutation();
  const updateMutation = useUpdateDeliveryDestinationMutation();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const columns: Column[] = [
    {
      Header: t('deliveryDestinations.customer'),
      id: 'customerName',
      accessor: 'customerName'
    },
    {
      Header: t('deliveryDestinations.name'),
      id: 'name',
      accessor: 'name'
    },
    {
      Header: t('deliveryDestinations.name2'),
      id: 'name2',
      accessor: 'name2'
    },
    {
      Header: t('deliveryDestinations.code'),
      id: 'code',
      accessor: 'code'
    },
    {
      Header: t('deliveryDestinations.reference'),
      id: 'reference',
      accessor: 'reference'
    },
    {
      Header: t('deliveryDestinations.streetAddress'),
      id: 'streetAddress',
      accessor: 'streetAddress'
    },
    {
      Header: t('deliveryDestinations.postalCode'),
      id: 'postalCode',
      accessor: 'postalCode'
    },
    {
      Header: t('deliveryDestinations.city'),
      id: 'city',
      accessor: 'city'
    },
    {
      Header: t('deliveryDestinations.country'),
      id: 'country',
      accessor: 'country'
    }
  ];

  const formKey = (selectedItem && selectedItem.id) || 'new';

  const handleRowSelected = (rowInfo: RowInfo) => {
    setSelectedItem(rowInfo.original);
    setIsEditorOpen(true);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      <AdminNavigation isEnabled={!isEditorOpen} isLoading={isLoading} fetch={() => refetch()} />
      <HeadingRow>
        <Button primary={true} disabled={isSaving} onClick={handleAddClick}>
          {t('add_new')}
        </Button>
        {(isLoading || isSaving) && <OperationLoader title={t('updating') + '...'} />}
      </HeadingRow>
      <Layout>
        <MainContent>
          <ContentContainer>
            <Table
              data={items}
              columns={columns}
              filterable={true}
              defaultSortColumn="name"
              defaultSortDesc={true}
              defaultPageSize={20}
              noDataText={t('deliveryDestinations.no_rows')}
              onRowSelected={handleRowSelected}
              rowSelector={i => isEditorOpen && selectedItem && selectedItem.id === i.original.id}
            />
          </ContentContainer>
        </MainContent>
        {isEditorOpen && (
          <SideBar>
            <DeliveryDestinationEditor
              key={formKey}
              deliveryDestinationToEdit={selectedItem}
              onClose={closeEditor}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default DeliveryDestinations;
